import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "../lib/apiClient";
import { useSeo } from "../lib/useSeo";
import { breederUrl } from "../lib/vendorLinks";
import PlatformPulse from "../components/PlatformPulse";
import UpcomingStrip from "../components/UpcomingStrip";
import HotBreedersStrip from "../components/HotBreedersStrip";
import ActivityTicker from "../components/ActivityTicker";
import MarketIntelligence from "../components/MarketIntelligence";
import VaultArchiveStrip from "../components/VaultArchiveStrip";
import LiveGrowOffsHero from "../components/LiveGrowOffsHero";
import BreederTierBadge from "../components/vendor/BreederTierBadge";
import SignupBonusPromoBanner from "../components/SignupBonusPromoBanner";
import PromoStripBanner from "../components/PromoStripBanner";
import HomeReferralWidget from "../components/HomeReferralWidget";
import DailySpinner from "../components/DailySpinner";
import { VaultBanner } from "../components/VaultCampaign";
import { usePlatform } from "../context/PlatformContext";
import { useAuth } from "../context/AuthContext";
import {
  ArrowRight, Shield, Truck, BadgeCheck, Sprout, Gavel, TrendingUp,
  Crown, Star, Users, BookOpen, Network, Layers, Compass, Hammer, Handshake,
  ShieldCheck, FileCheck2, Activity, Tag, Gift, Clock,
} from "lucide-react";
import FeedCard from "../components/feed/FeedCard";
import { listingUrl } from "../lib/urls";

const HERO_IMG = "/hero-seeds.jpg";

function timeShort(iso) {
  const ms = new Date(iso) - new Date();
  if (ms <= 0) return "ENDED";
  const totalSec = Math.floor(ms / 1000);
  const d = Math.floor(totalSec / 86400);
  const h = Math.floor((totalSec % 86400) / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  return d > 0 ? `${d}d ${h}h ${m}m` : `${String(h).padStart(2,"0")}:${String(m).padStart(2,"0")}:${String(s).padStart(2,"0")}`;
}

const fmt = (n) => (typeof n === "number" ? n.toLocaleString() : "—");

// v3.99c.59 — JSON-LD blobs injected once into <head> via inline <script>.
// `useEffect` mounts them. WebSite + Organization are both top-level entities
// SEO crawlers expect on the brand homepage.
function injectJsonLd() {
  const docs = [
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "CannaForge",
      "url": "https://thecannaforge.com",
      "logo": "https://thecannaforge.com/og-cover.jpg",
      "sameAs": ["https://twitter.com/cannaforge"],
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "7125 Headley ST SE #1038",
        "addressLocality": "Ada",
        "addressRegion": "MI",
        "postalCode": "49301",
        "addressCountry": "US",
      },
      "description": "CannaForge is a cannabis genetics infrastructure and discovery platform connecting collectors, growers, breeders, vendors, lineage records, traits, and trusted listings.",
    },
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "name": "CannaForge",
      "url": "https://thecannaforge.com",
    },
  ];
  const existing = document.getElementById("cf-home-jsonld");
  if (existing) existing.remove();
  const s = document.createElement("script");
  s.type = "application/ld+json";
  s.id = "cf-home-jsonld";
  s.text = JSON.stringify(docs);
  document.head.appendChild(s);
  return () => { document.getElementById("cf-home-jsonld")?.remove(); };
}

export default function Home() {
  const { public_auctions_enabled, bank_approval_mode } = usePlatform();
  const { user } = useAuth();
  const isInternal = user && (user.role === "admin" || user.role === "vendor");
  // v3.99c.59 — BAM is the *exception*, not the default. When BAM is off
  // (current production state), the full CannaForge platform is shown.
  // BAM only suppresses bank-restricted language when the toggle is on.
  const bam = bank_approval_mode && !isInternal;

  const [endingSoon, setEndingSoon] = useState([]);
  const [sold, setSold] = useState([]);
  const [breeders, setBreeders] = useState([]);
  const [stats, setStats] = useState(null);
  const [activeGiveaways, setActiveGiveaways] = useState([]);
  const [, tick] = useState(0);

  useSeo({
    title: "CannaForge | Cannabis Genetics, Lineage Records, Breeders & Grower Tools",
    description: "Explore cannabis genetics, lineage records, breeders, grower resources, trait pages, listings, and the interactive Genetics Map through CannaForge.",
    canonical: "/",
    ogImage: "https://thecannaforge.com/og-cover.jpg",
  });

  useEffect(() => {
    const cleanup = injectJsonLd();
    api.get("/public/homepage-stats").then(r => setStats(r.data)).catch(() => {});
    api.get("/marketplace/ending-soon", { params: { limit: 6 } }).then(r => setEndingSoon(r.data)).catch(() => {});
    api.get("/marketplace/recently-sold", { params: { limit: 12 } }).then(r => setSold(r.data)).catch(() => {});
    api.get("/vendors?section=premium").then(r => setBreeders(r.data)).catch(() => {});
    // v3.99d.286 — Right Now section: active giveaways feed.
    api.get("/giveaways", { params: { status: "active", limit: 3 } })
      .then(r => setActiveGiveaways(Array.isArray(r.data) ? r.data : (r.data?.items || [])))
      .catch(() => {});
    const id = setInterval(() => tick(n => n + 1), 1000);
    return () => { clearInterval(id); cleanup(); };
  }, []);

  // v3.99c.59 — Defensive: never let Forging Breeders leak into the homepage
  const verifiedBreeders = breeders.filter(
    (v) => v.breeder_tier !== "forging_breeder" && v.breeder_tier !== "collector_grower"
  );

  return (
    <div data-testid="home-page" className="flex flex-col min-w-0 [&>*]:min-w-0" style={{ minWidth: 0 }}>

      {/* v3.99d.363 — Signed-out $10 signup / $20 referral promo
          banner. Renders only for logged-out visitors so authed
          collectors don't see a redundant CTA (their dashboard has
          its own ribbon). Positioned above the hero so it's the
          first thing a new visitor sees. */}
      <SignupBonusPromoBanner />

      {/* v3.99d.404 — Free pack strip. Auto-rotates through live
          free / promo packs so the "free pack we offer" is
          impossible to miss — sits directly below the $10 Forge
          Funds banner and above the hero. Silently dissolves when
          no live offers exist. */}
      <PromoStripBanner testid="home-promo-strip" />

      {/* v3.99d.369 — Build the Vault urgency banner. Fetches
          admin-editable copy from /vault-campaign; renders nothing
          when the admin toggles `enabled=false`. */}
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 mt-3">
        <VaultBanner variant="home"/>
      </div>

      {/* v3.99d.412 — Active Giveaways strip. Renders directly under
          the Vault "timeline warning" so live giveaways are the
          first thing collectors see after the closing-window
          notice. Each thumbnail links to the giveaway detail page.
          Silent when nothing is live. Data source: the same
          `activeGiveaways` state used by RightNowSection below. */}
      {activeGiveaways && activeGiveaways.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-4 sm:px-6 mt-4"
                 data-testid="home-active-giveaways-strip">
          <div className="flex items-baseline justify-between mb-2">
            <div className="flex items-center gap-2">
              <span className="live-dot"/>
              <div className="label-eyebrow"
                   style={{ color: "var(--text-muted)" }}>
                Live Giveaways
              </div>
            </div>
            <Link to="/giveaways"
                  className="text-[11px] font-mono uppercase tracking-[0.18em] underline decoration-dotted underline-offset-4 opacity-70 hover:opacity-100"
                  style={{ color: "var(--text-muted)" }}
                  data-testid="home-active-giveaways-view-all">
              view all →
            </Link>
          </div>
          <div className="flex gap-3 overflow-x-auto pb-2" style={{ WebkitOverflowScrolling: "touch" }}>
            {activeGiveaways.slice(0, 4).map((g) => {
              const end = g.ends_at ? new Date(g.ends_at) : null;
              const diff = end ? end - Date.now() : 0;
              let countdown = "";
              if (end && diff > 0) {
                const h = Math.floor(diff / 3600000);
                const m = Math.floor((diff % 3600000) / 60000);
                const d = Math.floor(h / 24);
                if (d >= 1) countdown = `${d}d ${h % 24}h left`;
                else if (h >= 1) countdown = `${h}h ${m}m left`;
                else countdown = `${m}m left`;
              }
              const imgSrc = g.banner_url || g.prize_images?.[0];
              return (
                <Link key={g.id}
                      to={`/giveaways/${g.slug || g.id}`}
                      data-testid={`home-active-giveaway-${g.id}`}
                      className="group flex items-center gap-3 border flex-shrink-0 overflow-hidden hover:border-[var(--gold)]/60 transition-colors pr-3"
                      style={{ background: "var(--surface-2)",
                                borderColor: "var(--border)",
                                borderRadius: 2,
                                minWidth: "250px",
                                maxWidth: "340px" }}>
                  <div className="w-20 h-20 flex-shrink-0 overflow-hidden bg-black/40">
                    {imgSrc
                      ? <img src={imgSrc}
                             alt={g.title}
                             loading="lazy"
                             className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"/>
                      : <div className="w-full h-full flex items-center justify-center text-[9px]"
                             style={{ color: "var(--text-muted)" }}>
                          No image
                        </div>}
                  </div>
                  <div className="py-2.5 min-w-0 flex-1">
                    <div className="text-[10px] font-mono uppercase tracking-wider truncate"
                         style={{ color: "var(--gold)" }}>
                      {g.entry_cost === 0 || g.free_entry
                        ? "FREE ENTRY"
                        : g.entry_cost
                        ? `$${g.entry_cost} entry` : "ENTER"}
                    </div>
                    <div className="text-sm mt-0.5 line-clamp-2 leading-tight"
                         style={{ color: "var(--text)" }}
                         title={g.title}>
                      {g.title}
                    </div>
                    {countdown && (
                      <div className="text-[9px] font-mono mt-1"
                           style={{ color: "var(--text-muted)" }}>
                        {countdown}
                      </div>
                    )}
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
      )}

      {/* ──────────────────────────────────────────────────────────────────
          1. HERO
          ────────────────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover"
               width="1920" height="1080"
               fetchPriority="high"
               decoding="async"
               style={{filter: "brightness(0.50) saturate(0.85) sepia(0.15)"}}
               onError={(e)=>{e.target.src="https://images.unsplash.com/photo-1536819114556-1e10f967fb61?auto=format&fit=crop&w=2000&q=80";}}/>
          <div className="absolute inset-0" style={{background:"linear-gradient(180deg, rgba(8,9,11,0.78) 0%, rgba(8,9,11,0.55) 35%, rgba(8,9,11,0.88) 75%, var(--bg) 100%)"}}/>
          <div className="absolute inset-0" style={{background:"radial-gradient(ellipse at 28% 50%, rgba(10,61,42,0.35), transparent 60%)"}}/>
        </div>
        <div className="relative max-w-[1400px] mx-auto px-6 pt-20 pb-28 lg:pt-28 lg:pb-40">
          <motion.div initial={{opacity:0, y:20}} animate={{opacity:1, y:0}} transition={{duration:0.7}}
                      className="max-w-4xl">
            <div className="label-eyebrow mb-5 flex items-center gap-2">
              <span className="live-dot"/>{bam ? "Curated catalog · Approved brands" : "Live · Real drops · Real breeders"}
            </div>
            <h1 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-[5rem] leading-[0.95] tracking-tighter" style={{color:"var(--text)"}}>
              {bam ? (
                <>Curated genetics.<br/><span style={{color:"var(--primary-text)"}}>Approved brands.</span> One catalog.</>
              ) : (
                <>Rare packs.<br/><span style={{color:"var(--primary-text)"}}>Live bids.</span> Real drops.</>
              )}
            </h1>
            <p className="mt-7 max-w-2xl text-base md:text-lg leading-relaxed" style={{color:"var(--text-2)"}}>
              {bam
                ? "CannaForge is a curated cannabis genetics catalog featuring approved breeder brands, upcoming releases, collector accounts, giveaways, and lineage discovery."
                : "Auctions, buy-now, and giveaways from breeders you actually trust. Build the collection you've been hunting for."}
            </p>
            <div className="mt-9 flex flex-wrap gap-3 items-center" data-testid="home-hero-ctas">
              {/* v3.99d.405 — CTAs beefed for obvious-not-huge. Bump
                  padding, weight, and pair with an animated ring on
                  Shop Auctions so the eye lands there first. Buy Now
                  keeps its gold accent for contrast. */}
              <Link to="/auctions" className="btn-primary !py-3.5 !px-6 !text-sm font-bold tracking-wide relative shadow-[0_0_0_1px_rgba(219,120,90,0.4),0_8px_24px_-8px_rgba(219,120,90,0.55)] hover:shadow-[0_0_0_1px_rgba(219,120,90,0.7),0_12px_32px_-6px_rgba(219,120,90,0.75)] transition-shadow"
                    data-testid="hero-shop-auctions">
                <span className="live-dot mr-1"/>
                Shop Auctions <ArrowRight className="w-4 h-4"/>
              </Link>
              <Link to="/buy-now"
                    className="!py-3.5 !px-6 !text-sm font-bold tracking-wide border-2 inline-flex items-center gap-2 hover:brightness-125 transition-all"
                    style={{ borderColor: "var(--gold)",
                             color: "var(--gold)",
                             borderRadius: 2,
                             background: "rgba(212,175,55,0.06)" }}
                    data-testid="hero-shop-buy-now">
                Shop Buy Now
                <ArrowRight className="w-4 h-4"/>
              </Link>
              {!user && (
                <Link to="/register"
                      className="text-[11px] font-mono uppercase tracking-[0.18em] underline decoration-dotted underline-offset-4 opacity-70 hover:opacity-100 transition-opacity"
                      style={{color:"var(--text-muted)"}}
                      data-testid="hero-create-account">
                  or create a free collector account
                </Link>
              )}
            </div>

            {/* v3.99d.404 — Inline "Ending soon" ticker riding under
                the CTAs. Pulls the same feed as the Right Now section
                but renders it here compressed so the hook lands before
                the buyer scrolls. Silent when there's nothing live. */}
            {!bam && endingSoon.length > 0 && (
              <div className="mt-8 border-t border-[var(--border)] pt-5"
                   data-testid="hero-ending-soon-ticker">
                <div className="text-[11px] font-mono uppercase tracking-[0.22em] mb-3 flex items-center gap-2"
                     style={{color:"var(--text-muted)"}}>
                  <span className="live-dot"/> Ending soon
                </div>
                <div className="flex gap-3 overflow-x-auto pb-2"
                     style={{ WebkitOverflowScrolling: "touch",
                              touchAction: "pan-x" }}>
                  {endingSoon.slice(0, 4).map((a) => (
                    <Link key={a.id}
                          to={`/shop/${a.store_slug || a.vendor_slug || 'store'}/${a.product_slug || a.slug || a.id}`}
                          className="flex items-center gap-3 border p-2 pr-4 flex-shrink-0 min-w-[220px] hover:border-[var(--gold)]/60 transition-colors"
                          style={{ background: "rgba(8,9,11,0.55)",
                                   borderColor: "var(--border)",
                                   backdropFilter: "blur(6px)",
                                   borderRadius: 2 }}
                          data-testid={`hero-ending-soon-${a.id}`}>
                      <img src={a.images?.[0]} alt=""
                           className="w-12 h-12 object-cover flex-shrink-0"
                           loading="lazy"/>
                      <div className="min-w-0 flex-1">
                        <div className="text-[11px] font-mono uppercase tracking-wider truncate"
                             style={{color:"var(--text-muted)"}}
                             title={a.store_name}>
                          {a.store_name || "\u00a0"}
                        </div>
                        <div className="text-sm truncate"
                             style={{color:"var(--text)"}}
                             title={a.title || a.strain_name}>
                          {a.title || a.strain_name}
                        </div>
                        <div className="flex items-baseline gap-2 mt-0.5">
                          <span className="font-mono text-sm"
                                style={{color:"var(--gold)"}}>
                            ${Number(a.current_bid || a.price || 0).toFixed(0)}
                          </span>
                          <span className="text-[10px] font-mono uppercase tracking-wider"
                                style={{color:"var(--text-muted)"}}>
                            {(() => {
                              const end = a.auction_ends_at ? new Date(a.auction_ends_at) : null;
                              if (!end) return "";
                              const diff = end - Date.now();
                              if (diff <= 0) return "ending";
                              const h = Math.floor(diff / 3600000);
                              const m = Math.floor((diff % 3600000) / 60000);
                              if (h >= 24) return `${Math.floor(h/24)}d ${h%24}h`;
                              if (h >= 1) return `${h}h ${m}m`;
                              return `${m}m`;
                            })()}
                          </span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-6 text-[11px] font-mono uppercase tracking-[0.18em]" style={{color:"var(--text-muted)"}} data-testid="hero-microcopy">
              Bid live · Buy now · Enter giveaways
            </div>

            {/* v3.99d.364 — Compact share-your-link widget for
                authenticated collectors browsing the homepage. Renders
                nothing for logged-out visitors (they see the top
                SignupBonusPromoBanner instead). */}
            <HomeReferralWidget />

            {/* Hero live ribbon — only show stats that have real data */}
            <div className="mt-12 flex flex-wrap gap-x-8 gap-y-3 text-xs font-mono" style={{color:"var(--text-2)"}}>
              {stats?.public_strain_count > 0 && <Stat label="Lineage records" value={fmt(stats.public_strain_count)}/>}
              {stats?.breeder_count > 0 && <Stat label={bam ? "Featured brands" : "Verified breeders"} value={stats.breeder_count}/>}
              {stats?.live_listing_count > 0 && <Stat label="Listings live" value={stats.live_listing_count}/>}
              {stats?.active_giveaway_count > 0 && <Stat label="Active giveaways" value={stats.active_giveaway_count}/>}
            </div>
          </motion.div>
        </div>
      </section>

      {/* v3.99d.287 — Activity tickers MOVED out of the above-the-fold
          slot and consolidated into the "Community & marketplace
          activity" section further down. The engagement engine is now
          the first beat after the hero. */}

      {/* ──────────────────────────────────────────────────────────────────
          1. HERO  →  2. RIGHT NOW ON CANNAFORGE
          v3.99d.287 Phase 1A consolidation:
          Right Now appears directly under the hero. The two activity
          tickers that previously sat between the hero and Right Now
          were folded into the lower "Community & Marketplace Activity"
          section so the engagement engine becomes the first major
          beat after the headline.
          ────────────────────────────────────────────────────────────────── */}
      <RightNowSection
        endingSoon={endingSoon}
        activeGiveaways={activeGiveaways}
        publicAuctionsEnabled={public_auctions_enabled}
      />

      {/* v3.99d.405 — Daily reward spinner. Authenticated collectors
          only; renders nothing for logged-out visitors so the sign-up
          promo strip up top stays the primary hook. */}
      {user && (
        <section className="max-w-[1400px] mx-auto px-4 sm:px-6 -mt-4 mb-12"
                 data-testid="home-daily-spinner">
          <DailySpinner />
        </section>
      )}

      {/* ──────────────────────────────────────────────────────────────────
          3. ENDING SOON AUCTIONS — replaces Featured Genetics.
          ────────────────────────────────────────────────────────────────── */}
      {endingSoon.length > 0 && (
        <section className="bg-[var(--bg-2)] py-16 border-y border-[var(--border)]" data-testid="home-ending-soon-section">
          <div className="max-w-[1400px] mx-auto px-6">
            <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
              <div>
                <div className="label-eyebrow mb-2 flex items-center gap-2">
                  <span className="live-dot"/>Ending soon
                </div>
                <h2 className="font-display text-3xl md:text-4xl tracking-tighter" style={{color:"var(--text)"}}>
                  Live auctions closing soon
                </h2>
              </div>
              <Link to="/auctions" className="btn-outline" data-testid="home-ending-soon-view-all">
                All auctions <ArrowRight className="w-4 h-4 ml-1 inline"/>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 xl:grid-cols-3 gap-4 md:gap-5"
                 data-testid="home-ending-soon-grid">
              {endingSoon.slice(0, 6).map((a, i) => (
                <EndingSoonCard key={a.id} auction={a} index={i} />
              ))}
            </div>
          </div>
        </section>
      )}
      <VaultArchiveStrip />

      {/* ──────────────────────────────────────────────────────────────────
          4. FEATURED BREEDERS — moved UP from prior position 10.
          ────────────────────────────────────────────────────────────────── */}
      {verifiedBreeders.length > 0 && (
        <section className="w-full max-w-[1400px] mx-auto px-4 sm:px-6 py-12 sm:py-16 min-w-0" data-testid="home-breeders-section" style={{ minWidth: 0 }}>
          <div className="flex items-end justify-between mb-6 sm:mb-8 flex-wrap gap-4">
            <div className="min-w-0">
              <div className="label-eyebrow mb-2">Curated breeder roster</div>
              <h2 className="font-display text-2xl sm:text-3xl md:text-4xl tracking-tighter break-words" style={{color:"var(--text)"}}>
                Verified breeders, hand-onboarded
              </h2>
              <p className="text-sm mt-2 max-w-xl" style={{color:"var(--text-2)"}}>
                Forge Master, Forge Partner, and Forged Breeder tiers only. Forging Breeders live on the full directory page.
              </p>
            </div>
            <Link to="/breeders" className="btn-outline">All breeders <ArrowRight className="w-4 h-4 ml-1 inline"/></Link>
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2 sm:gap-3 md:gap-4">
            {verifiedBreeders.slice(0, 6).map((v, i) => {
              const isFounding = !!v.founding_breeder || (v.badges || []).includes("founding");
              return (
                <motion.div key={v.id}
                            className="min-w-0"
                            initial={{ opacity: 0, y: 8 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.3, delay: Math.min(i, 12) * 0.04 }}>
                  <Link to={breederUrl(v)}
                        className="block min-w-0 bg-[var(--surface)] border border-[var(--border)] hover:border-[var(--primary-text)] transition-all group overflow-hidden"
                        data-testid={`breeder-tile-${v.store_slug || v.id}`}>
                    <div className="aspect-square bg-black flex items-center justify-center p-3 sm:p-4 md:p-5 border-b border-[var(--border)] relative overflow-hidden min-w-0">
                      {v.banner_url && (<img src={v.banner_url} alt="" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover opacity-50 group-hover:opacity-60 transition-opacity"/>)}
                      <div className="absolute inset-0" style={{background:"radial-gradient(ellipse at center, rgba(10,61,42,0.35), transparent 70%)"}}/>
                      {v.logo_url && (<img src={v.logo_url} alt={v.store_name} loading="lazy" decoding="async" className="relative max-w-[78%] max-h-[78%] object-contain group-hover:scale-105 transition-transform duration-500" style={{minWidth:0}}/>)}
                      {isFounding && (
                        <span className="absolute top-2 left-2 inline-flex items-center gap-1 px-1.5 py-1 font-mono uppercase text-[9px] tracking-[0.18em]"
                              style={{background:"rgba(212,175,55,0.18)", color:"var(--gold)", border:"1px solid rgba(212,175,55,0.45)", borderRadius:2}}>
                          <Crown className="w-3 h-3" /> Founding
                        </span>
                      )}
                      {(v.breeder_tier === "forge_partner" || v.breeder_tier === "forge_master") && (
                        <div className="absolute top-2 right-2">
                          <BreederTierBadge tier={v.breeder_tier} size="sm" showLabel={false} />
                        </div>
                      )}
                    </div>
                    <div className="p-2 sm:p-3 min-w-0">
                      <div className="flex items-center gap-1 mb-0.5 min-w-0">
                        <span className="font-display text-[12px] sm:text-[14px] md:text-[15px] leading-tight truncate min-w-0 flex-1" style={{color:"var(--text)"}}>{v.store_name}</span>
                        <BadgeCheck className="w-3 h-3 sm:w-3.5 sm:h-3.5 flex-shrink-0" style={{color:"var(--primary-text)"}}/>
                      </div>
                      {v.tagline ? (
                        <div className="hidden sm:block text-[9px] font-mono uppercase tracking-[0.16em] line-clamp-2 leading-snug" style={{color:"var(--gold)"}}>{v.tagline}</div>
                      ) : v.location ? (
                        <div className="hidden sm:block text-[10px]" style={{color:"var(--text-muted)"}}>{v.location}</div>
                      ) : null}
                      <div className="text-[9px] sm:text-[10px] font-mono mt-1 inline-flex items-center gap-1" style={{color:"var(--text-muted)"}}>
                        <Users className="w-2.5 h-2.5" />{v.follower_count || 0} follower{(v.follower_count || 0) === 1 ? "" : "s"}
                      </div>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
          <div className="mt-10"><HotBreedersStrip limit={10} /></div>
        </section>
      )}

      {/* ──────────────────────────────────────────────────────────────────
          5. CHOOSE YOUR PATH — audience-segmented entry points.
          ────────────────────────────────────────────────────────────────── */}
      <ExplorePathsSection />

      {/* ──────────────────────────────────────────────────────────────────
          6. EXPLORE GENETICS / LINEAGE ATLAS
             The previous FeatureShowcase block presented four equal
             feature tiles. Reduced to the lineage / genetics story to
             stop competing with the engagement engine above.
          ────────────────────────────────────────────────────────────────── */}
      <FeatureShowcase stats={stats} />

      {/* ──────────────────────────────────────────────────────────────────
          7. COMMUNITY & MARKETPLACE ACTIVITY
             Combines the live-activity ticker, recently-sold ticker,
             platform pulse, market-intel, and live grow-offs into one
             cohesive activity section instead of five competing
             standalone bands above the fold.
          ────────────────────────────────────────────────────────────────── */}
      <section className="border-t border-[var(--border)]" data-testid="home-community-activity">
        <div className="max-w-[1400px] mx-auto px-6 pt-12">
          <div className="label-eyebrow mb-2 flex items-center gap-2">
            <Activity className="w-3 h-3" />
            On the platform
          </div>
          <h2 className="font-display text-3xl md:text-4xl tracking-tighter mb-2" style={{color:"var(--text)"}}>
            Community &amp; marketplace activity
          </h2>
        </div>
        {/* Single combined ticker — was previously two side-by-side */}
        <div className="mt-4"><ActivityTicker pulse="all" /></div>
        {/* Recently-sold proof — only when there's history */}
        {sold.length > 0 && (
          <div className="border-y border-[var(--border)] bg-[var(--surface)] py-3 overflow-hidden">
            <div className="ticker whitespace-nowrap">
              {[...sold, ...sold].map((s, i) => (
                <span key={i} className="text-xs flex items-center gap-2 flex-shrink-0" style={{color:"var(--text-2)"}}>
                  <TrendingUp className="w-3 h-3" style={{color:"var(--primary-text)"}}/>
                  <span style={{color:"var(--text-muted)"}}>SOLD</span>
                  <span style={{color:"var(--text)"}} className="font-semibold">{s.title}</span>
                  <span style={{color:"var(--text-muted)"}}>·</span>
                  <span style={{color:"var(--gold)"}} className="font-mono">${s.price.toFixed(2)}</span>
                  <span style={{color:"var(--text-muted)"}}>· {s.store_name}</span>
                </span>
              ))}
            </div>
          </div>
        )}
        <PlatformPulse />
        <LiveGrowOffsHero />
        <MarketIntelligence />
      </section>

      {/* ──────────────────────────────────────────────────────────────────
          8. FORGE FEED — community proof, drives signup.
          ────────────────────────────────────────────────────────────────── */}
      <HomeForgeFeedSection bam={bam} />

      {/* ──────────────────────────────────────────────────────────────────
          11. FINAL CTA — Two clear next steps
          ────────────────────────────────────────────────────────────────── */}
      <section className="border-t border-[var(--border)] bg-[var(--surface)]" data-testid="home-final-cta">
        <div className="max-w-[1400px] mx-auto px-6 py-16 grid md:grid-cols-2 gap-8">
          <div className="p-6 md:p-8 border border-[var(--border)] bg-[var(--bg-2)]">
            <Sprout className="w-7 h-7 mb-3" style={{color:"var(--primary-text)"}}/>
            <div className="label-eyebrow mb-2">Collectors &amp; Growers</div>
            <h3 className="font-display text-2xl md:text-3xl tracking-tight" style={{color:"var(--text)"}}>Start with a free account</h3>
            <p className="text-sm mt-3 leading-relaxed" style={{color:"var(--text-2)"}}>
              Follow breeders, track your collection, save strains, browse lineage records, enter giveaways, and watch upcoming drops.
              Collector and grower accounts are <strong style={{color:"var(--text)"}}>free to start</strong>.
            </p>
            <Link to="/register" className="btn-primary inline-flex mt-5" data-testid="final-cta-register">
              Create Free Account <ArrowRight className="w-4 h-4 ml-1"/>
            </Link>
          </div>
          <div className="p-6 md:p-8 border border-[var(--gold)] bg-[var(--bg-2)]">
            <Hammer className="w-7 h-7 mb-3" style={{color:"var(--gold)"}}/>
            <div className="label-eyebrow mb-2" style={{color:"var(--gold)"}}>Breeders &amp; Vendors</div>
            <h3 className="font-display text-2xl md:text-3xl tracking-tight" style={{color:"var(--text)"}}>Apply through the tiered trust system</h3>
            <p className="text-sm mt-3 leading-relaxed" style={{color:"var(--text-2)"}}>
              Build a breeder profile, connect genetics to lineage records, unlock listings as your catalog matures.
              Breeder/vendor marketplace tools and premium features require approval; some have paid plans.
            </p>
            <div className="flex flex-wrap gap-2 mt-5">
              <Link to="/for-breeders" className="btn-primary inline-flex" data-testid="final-cta-breeder">
                See the breeder platform <ArrowRight className="w-4 h-4 ml-1"/>
              </Link>
              <Link to="/join/breeder" className="btn-outline inline-flex text-xs">Or apply to launch</Link>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────

function Stat({ label, value }) {
  return (
    <div className="flex items-center gap-2">
      <span className="font-mono text-base font-bold" style={{color:"var(--primary-text)"}}>{value}</span>
      <span className="uppercase tracking-wider text-[10px]" style={{color:"var(--text-muted)"}}>{label}</span>
    </div>
  );
}

function EndingSoonCard({ auction, index }) {
  const a = auction;
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick(n => n + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const end = a.auction_ends_at ? new Date(a.auction_ends_at) : null;
  const diff = end ? end - Date.now() : 0;
  const urgent = diff > 0 && diff < 3600000;
  let countdown = "";
  if (end && diff > 0) {
    const totalSec = Math.floor(diff / 1000);
    const d = Math.floor(totalSec / 86400);
    const h = Math.floor((totalSec % 86400) / 3600);
    const m = Math.floor((totalSec % 3600) / 60);
    const s = totalSec % 60;
    if (d > 0) countdown = `${d}d ${h}h ${m}m`;
    else countdown = `${String(h).padStart(2,"0")}:${String(m).padStart(2,"0")}:${String(s).padStart(2,"0")}`;
  } else {
    countdown = "ENDED";
  }

  const href = `/shop/${a.store_slug || a.vendor_slug || 'store'}/${a.product_slug || a.slug || a.id}`;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.3, delay: Math.min(index, 5) * 0.06 }}
    >
      <Link
        to={href}
        className="group block border overflow-hidden hover:border-[var(--primary-text)]/60 transition-colors"
        style={{ background: "var(--surface)", borderColor: "var(--border)", borderRadius: 2 }}
        data-testid={`ending-soon-card-${a.id}`}
      >
        <div className="aspect-square overflow-hidden bg-black/40 relative">
          {a.images?.[0]
            ? <img src={a.images[0]} alt={a.title || a.strain_name}
                   loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"/>
            : <div className="w-full h-full flex items-center justify-center text-[10px]"
                   style={{ color: "var(--text-muted)" }}>No image</div>}
          <div className="absolute bottom-0 inset-x-0 p-2 flex items-center justify-between"
               style={{ background: "linear-gradient(transparent, rgba(0,0,0,0.85))" }}>
            <div className={`text-[11px] font-mono font-bold tracking-wider ${urgent ? "text-red-400 animate-pulse" : ""}`}
                 style={urgent ? {} : { color: "var(--text)" }}>
              <Clock className="w-3 h-3 inline mr-1 -mt-px" />
              {countdown}
            </div>
            {a.bid_count > 0 && (
              <div className="text-[10px] font-mono" style={{ color: "var(--text-muted)" }}>
                {a.bid_count} bid{a.bid_count === 1 ? "" : "s"}
              </div>
            )}
          </div>
        </div>
        <div className="p-3">
          <div className="text-[10px] font-mono uppercase tracking-[0.16em] truncate"
               style={{ color: "var(--text-muted)" }}>
            {a.store_name || a.vendor_name || ""}
          </div>
          <div className="text-sm font-display mt-0.5 line-clamp-1 leading-tight group-hover:text-[var(--primary-text)] transition-colors"
               style={{ color: "var(--text)" }}
               title={a.title || a.strain_name}>
            {a.title || a.strain_name}
          </div>
          <div className="flex items-baseline gap-2 mt-1.5">
            <span className="text-[10px] font-mono uppercase tracking-wider"
                  style={{ color: "var(--text-muted)" }}>Current bid</span>
            <span className="font-mono text-base font-bold"
                  style={{ color: "var(--primary-text)" }}>
              ${Number(a.current_bid || a.starting_bid || 0).toFixed(2)}
            </span>
          </div>
        </div>
      </Link>
    </motion.div>
  );
}

function StatCard({ icon: Icon, label, value, hint, href }) {
  const inner = (
    <>
      <Icon className="w-5 h-5 mb-3" style={{color:"var(--gold)"}}/>
      <div className="font-mono text-3xl md:text-4xl font-bold" style={{color:"var(--text)"}}>{value}</div>
      <div className="text-xs mt-1.5 uppercase tracking-wider" style={{color:"var(--text-muted)"}}>{label}</div>
      {hint && <div className="text-[11px] mt-1" style={{color:"var(--text-2)"}}>{hint}</div>}
    </>
  );
  const testId = `stat-${label.toLowerCase().replace(/[^a-z0-9]+/g,'-')}`;
  if (href) {
    return (
      <Link to={href} data-testid={testId}
            className="p-5 bg-[var(--surface)] border border-[var(--border)] hover:border-[var(--gold)] transition-colors block">
        {inner}
      </Link>
    );
  }
  return <div className="p-5 bg-[var(--surface)] border border-[var(--border)]" data-testid={testId}>{inner}</div>;
}

function LiveStatsSection({ stats }) {
  // Build the card list dynamically, omitting any field that's zero/missing.
  const cards = [];
  if (stats?.public_strain_count > 0)   cards.push({ icon: FileCheck2, label: "Public lineage records", value: fmt(stats.public_strain_count), href: "/lineage" });
  if (stats?.canonical_strain_count > 0) cards.push({ icon: BadgeCheck, label: "Canonical genetics",      value: fmt(stats.canonical_strain_count), href: "/lineage?tab=archive" });
  if (stats?.breeder_count > 0)         cards.push({ icon: ShieldCheck, label: "Verified breeders",       value: fmt(stats.breeder_count), href: "/breeders" });
  if (stats?.live_listing_count > 0)    cards.push({ icon: Sprout,     label: "Live listings",            value: fmt(stats.live_listing_count), href: "/shop" });
  if (stats?.trait_page_count > 0)      cards.push({ icon: Layers,     label: "Trait & family pages",     value: fmt(stats.trait_page_count + (stats.family_page_count||0)), href: "/families" });
  if (stats?.active_giveaway_count > 0) cards.push({ icon: Gavel,      label: "Active giveaways",         value: fmt(stats.active_giveaway_count), href: "/giveaways" });

  if (cards.length === 0) {
    return (
      <section className="max-w-[1400px] mx-auto px-6 py-12" data-testid="home-live-stats">
        <div className="label-eyebrow mb-2">Live numbers</div>
        <h2 className="font-display text-3xl md:text-4xl tracking-tighter" style={{color:"var(--text)"}}>
          Growing daily.
        </h2>
        <p className="text-sm mt-2" style={{color:"var(--text-muted)"}}>
          The CannaForge platform is live and adding lineage records, breeders, and tools every week.
        </p>
      </section>
    );
  }

  return (
    <section className="max-w-[1400px] mx-auto px-6 py-14" data-testid="home-live-stats">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="label-eyebrow mb-2 flex items-center gap-2"><span className="live-dot"/>Live numbers</div>
          <h2 className="font-display text-3xl md:text-4xl tracking-tighter" style={{color:"var(--text)"}}>What's on CannaForge right now</h2>
        </div>
        <div className="text-[10px] font-mono uppercase tracking-[0.18em]" style={{color:"var(--text-muted)"}}>
          Real counts · no padding
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
        {cards.map((c) => <StatCard key={c.label} {...c} />)}
      </div>
    </section>
  );
}

function ExplorePathsSection() {
  return (
    <section className="bg-[var(--bg-2)] border-y border-[var(--border)] py-16" data-testid="home-explore-paths">
      <div className="max-w-[1400px] mx-auto px-6">
        <div className="mb-10">
          <div className="label-eyebrow mb-2">Explore CannaForge</div>
          <h2 className="font-display text-3xl md:text-4xl tracking-tighter" style={{color:"var(--text)"}}>Three ways in</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          {[
            {
              icon: Sprout, color: "var(--primary-text)",
              eyebrow: "Collectors & Growers",
              title: "Track every cut.",
              body: "Follow breeders, save strains, track your collection, enter giveaways, browse lineage records, and use Trait Matrix + Genetics Map for genetics research.",
              cta: { to: "/register", label: "Create Free Account" },
              alt: { to: "/browse", label: "Browse genetics first" },
            },
            {
              icon: Hammer, color: "var(--gold)",
              eyebrow: "Breeders & Vendors",
              title: "Free profile. Add your genetics.",
              body: "Claim your free Forging Breeder profile in seconds. Add your brand, story, signature strains, and lineage. Promote to a verified tier and unlock listings, drops, and auctions when you're ready.",
              cta: { to: "/join/breeder", label: "Claim Free Breeder Profile" },
              alt: { to: "/for-breeders", label: "See what breeders get" },
            },
            {
              icon: Network, color: "var(--secondary)",
              eyebrow: "Genetics & Lineage",
              title: "Discover the source.",
              body: "Public lineage records, canonical staples, family pages, trait pages, terpenes, aromas, classifications, and the interactive Genetics Map — all one click apart.",
              cta: { to: "/lineage", label: "Open Lineage Atlas" },
              alt: { to: "/lineage?tab=matrix", label: "Explore Trait Matrix" },
            },
          ].map((p) => {
            const Icon = p.icon;
            return (
              <div key={p.eyebrow} className="p-6 bg-[var(--surface)] border border-[var(--border)] hover:border-[var(--primary-text)] transition-colors flex flex-col"
                   data-testid={`explore-path-${p.eyebrow.toLowerCase().replace(/[^a-z]+/g,'-')}`}>
                <Icon className="w-7 h-7 mb-4" style={{color: p.color}}/>
                <div className="label-eyebrow mb-2" style={{color: p.color}}>{p.eyebrow}</div>
                <h3 className="font-display text-2xl tracking-tight mb-3" style={{color:"var(--text)"}}>{p.title}</h3>
                <p className="text-sm leading-relaxed flex-1" style={{color:"var(--text-2)"}}>{p.body}</p>
                <div className="mt-5 flex flex-col gap-2">
                  <Link to={p.cta.to} className="btn-primary inline-flex">{p.cta.label} <ArrowRight className="w-4 h-4 ml-1"/></Link>
                  <Link to={p.alt.to} className="text-xs underline" style={{color:"var(--text-muted)"}}>{p.alt.label}</Link>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function FeatureShowcase({ stats }) {
  const features = [
    {
      icon: Network,
      eyebrow: "Lineage Atlas",
      title: "Lineage records, documented.",
      body: "Browse public lineage records covering canonical staples, founding genetics, family lines, and breeder-confirmed cuts. Each record tracks parentage, aliases, reported vs disputed lineage, and breeder connections.",
      examples: [
        { label: "GMO", slug: "gmo" },
        { label: "Sour Diesel", slug: "sour-diesel" },
        { label: "OG Kush", slug: "og-kush" },
        { label: "Blue Dream", slug: "blue-dream" },
        { label: "Runtz", slug: "runtz" },
        { label: "Gelato", slug: "gelato" },
        { label: "Chemdog", slug: "chemdog" },
        { label: "Skunk #1", slug: "skunk-1" },
        { label: "Northern Lights", slug: "northern-lights" },
      ],
      cta: { to: "/lineage", label: "Explore Lineage Atlas" },
      stat: stats?.public_strain_count > 0 ? `${fmt(stats.public_strain_count)} lineage records` : null,
    },
    {
      icon: Compass,
      eyebrow: "Genetics Map",
      title: "Visualize the family tree.",
      body: "Interactive lineage graph — parent/child connections, canonical staples highlighted, live listings flagged, strain pop-cards, family/trait links, mobile-safe pan & zoom.",
      cta: { to: "/lineage?tab=map", label: "Open Genetics Map" },
      stat: null,
    },
    {
      icon: Layers,
      eyebrow: "Trait Matrix",
      title: "Explore by trait, not just name.",
      body: "Find genetics by family, terpene, aroma, classification, seed type, plant structure, and preservation status. Each trait page connects education, strain records, available listings, and breeders.",
      cta: { to: "/lineage?tab=matrix", label: "Explore Trait Matrix" },
      stat: stats && (stats.trait_page_count + (stats.family_page_count||0)) > 0
        ? `${fmt(stats.trait_page_count + (stats.family_page_count||0))} trait & family pages`
        : null,
    },
    {
      icon: BookOpen,
      eyebrow: "Grower Resources",
      title: "Genetics Guide & grow knowledge.",
      body: "Germination, watering, lighting, training, pH/EC, IPM, vegetative stage, lineage terms, breeding basics — the grower's reference layer for the CannaForge platform.",
      cta: { to: "/grower-resources", label: "Read Grower Resources" },
      stat: stats?.resource_article_count > 0 ? `${fmt(stats.resource_article_count)} articles` : null,
    },
  ];

  return (
    <section className="max-w-[1400px] mx-auto px-6 py-16" data-testid="home-feature-showcase">
      <div className="mb-10">
        <div className="label-eyebrow mb-2">The CannaForge layer</div>
        <h2 className="font-display text-3xl md:text-4xl tracking-tighter" style={{color:"var(--text)"}}>Built for genetics research, not just shopping</h2>
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        {features.map((f) => {
          const Icon = f.icon;
          return (
            <div key={f.eyebrow} className="p-6 md:p-8 bg-[var(--surface)] border border-[var(--border)] hover:border-[var(--gold)] transition-colors"
                 data-testid={`feature-${f.eyebrow.toLowerCase().replace(/[^a-z]+/g,'-')}`}>
              <div className="flex items-start justify-between mb-4 gap-3">
                <Icon className="w-8 h-8" style={{color:"var(--gold)"}}/>
                {f.stat && (
                  <span className="font-mono text-[10px] uppercase tracking-[0.18em] px-2 py-1 border"
                        style={{color:"var(--gold)", borderColor:"var(--border-strong)"}}>
                    {f.stat}
                  </span>
                )}
              </div>
              <div className="label-eyebrow mb-2" style={{color:"var(--gold)"}}>{f.eyebrow}</div>
              <h3 className="font-display text-2xl md:text-3xl tracking-tight mb-3" style={{color:"var(--text)"}}>{f.title}</h3>
              <p className="text-sm md:text-base leading-relaxed" style={{color:"var(--text-2)"}}>{f.body}</p>
              {f.examples && (
                <div className="mt-4 flex flex-wrap gap-1.5" data-testid={`feature-examples-${f.eyebrow.toLowerCase().replace(/[^a-z]+/g,'-')}`}>
                  {f.examples.map((ex) => (
                    <Link key={ex.slug}
                          to={`/strains/${ex.slug}`}
                          className="text-[11px] font-mono px-2 py-0.5 bg-[var(--bg)] border border-[var(--border)] hover:border-[var(--gold)] hover:text-[var(--text)] transition-colors"
                          style={{color:"var(--text-2)"}}
                          data-testid={`feature-example-${ex.slug}`}>
                      {ex.label}
                    </Link>
                  ))}
                </div>
              )}
              <Link to={f.cta.to} className="btn-outline inline-flex mt-5">{f.cta.label} <ArrowRight className="w-4 h-4 ml-1"/></Link>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function TrustTierSection({ stats }) {
  const tiers = [
    { id: "forging_breeder", icon: Hammer,     label: "Forging Breeder", desc: "Early-stage breeders building catalog and genetics for future review. Limited profile, no marketplace listings yet.", count: stats?.forging_breeder_count },
    { id: "forged_breeder",  icon: ShieldCheck, label: "Forged Breeder",  desc: "Approved breeder profile on CannaForge. Full marketplace access, listings, drops, and lineage tools.", count: stats?.forged_breeder_count },
    { id: "forge_partner",   icon: Handshake,   label: "Forge Partner",   desc: "Verified partner brand — seed banks, multi-breeder catalogs, strategic partners with expanded storefront.", count: stats?.forge_partner_count },
    { id: "forge_master",    icon: Crown,       label: "Forge Master",    desc: "Top-tier trusted breeder. Highest directory priority, featured placements, premium support.", count: stats?.forge_master_count },
  ];
  return (
    <section className="bg-[var(--bg-2)] border-y border-[var(--border)] py-16" data-testid="home-trust-tier">
      <div className="max-w-[1400px] mx-auto px-6">
        <div className="grid md:grid-cols-3 gap-10 mb-10">
          <div className="md:col-span-1">
            <Shield className="w-7 h-7 mb-3" style={{color:"var(--primary-text)"}}/>
            <div className="label-eyebrow mb-2">Trust &amp; transparency</div>
            <h2 className="font-display text-3xl md:text-4xl tracking-tighter" style={{color:"var(--text)"}}>
              Built around tiered trust.
            </h2>
          </div>
          <div className="md:col-span-2">
            <p className="text-sm md:text-base leading-relaxed" style={{color:"var(--text-2)"}}>
              CannaForge is built around transparency. Lineage records can be labeled as <strong style={{color:"var(--text)"}}>reported</strong>,{" "}
              <strong style={{color:"var(--text)"}}>disputed</strong>, <strong style={{color:"var(--text)"}}>verified</strong>,{" "}
              <strong style={{color:"var(--text)"}}>breeder-confirmed</strong>, or <strong style={{color:"var(--text)"}}>canonical</strong>.
              Breeder visibility is tiered so early-stage projects don't pollute the main marketplace while trusted brands receive stronger placement.
            </p>
            <p className="text-sm mt-3" style={{color:"var(--text-muted)"}}>
              Breeders apply once, then climb the tiers as their catalog, branding, and reputation mature.
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {tiers.map((t) => {
            const Icon = t.icon;
            return (
              <div key={t.id} className="p-5 bg-[var(--surface)] border border-[var(--border)] flex flex-col"
                   data-testid={`tier-card-${t.id}`}>
                <div className="flex items-start justify-between mb-3 gap-2">
                  <Icon className="w-6 h-6" style={{color:"var(--gold)"}}/>
                  {typeof t.count === "number" && t.count > 0 && (
                    <span className="font-mono text-xl font-bold" style={{color:"var(--text)"}}>{t.count}</span>
                  )}
                </div>
                <div className="font-display text-lg tracking-tight mb-2" style={{color:"var(--text)"}}>{t.label}</div>
                <p className="text-xs leading-relaxed flex-1" style={{color:"var(--text-2)"}}>{t.desc}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}


// v3.99c.72 — Homepage Forge Feed teaser. Pulls the 6 trending feed cards
// to show a logged-out visitor what's actually happening on the platform
// right now. Tied to /forge-feed for the full experience.
function HomeForgeFeedSection({ bam }) {
  const { user } = useAuth();
  const [items, setItems] = React.useState([]);
  const [loaded, setLoaded] = React.useState(false);
  React.useEffect(() => {
    let alive = true;
    api.get("/feed", { params: { sort: "trending", page_size: 6 } })
      .then((r) => { if (alive) { setItems(r.data?.items || []); setLoaded(true); } })
      .catch(() => { if (alive) setLoaded(true); });
    return () => { alive = false; };
  }, []);
  if (loaded && items.length === 0) return null;
  return (
    <section className="border-t border-[var(--border)] bg-[var(--bg-2)] min-w-0"
             data-testid="home-forge-feed-section" style={{ minWidth: 0 }}>
      <div className="w-full max-w-[1400px] mx-auto px-4 sm:px-6 py-12 sm:py-16 min-w-0">
        <div className="flex items-end justify-between gap-4 flex-wrap mb-3">
          <div className="min-w-0">
            <div className="label-eyebrow mb-2 inline-flex items-center gap-2">
              <Activity className="w-3.5 h-3.5" style={{ color: "var(--gold)" }} />
              Live · Activity Feed
            </div>
            <h2 className="font-serif text-2xl sm:text-3xl md:text-5xl tracking-tighter break-words"
                style={{ color: "var(--text)" }}>
              See what&rsquo;s happening on Forge Feed
            </h2>
          </div>
          <Link to="/forge-feed"
                className="btn-outline inline-flex items-center gap-1.5"
                data-testid="home-forge-feed-cta-view">
            View Forge Feed <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <p className="text-sm md:text-base max-w-3xl mb-8" style={{ color: "var(--text-2)" }}>
          Breeder updates, grow logs, new genetics, giveaways, auctions, articles,
          reviews, and lineage activity — all flowing through one live CannaForge feed.
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4"
             data-testid="home-forge-feed-grid">
          {items.slice(0, 6).map((it) => (
            <FeedCard key={it.id} item={it} isAuthed={!!user}
                      onAuthRequired={() => { window.location.href = "/register?next=%2Fforge-feed"; }} />
          ))}
        </div>
        {!user && (
          <div className="mt-8 border border-[var(--gold)]/40 bg-[var(--surface)] p-6 md:p-8"
               data-testid="home-forge-feed-gateway">
            <div className="flex flex-col md:flex-row md:items-center gap-4">
              <div className="flex-1">
                <div className="font-display text-xl md:text-2xl mb-1"
                     style={{ color: "var(--text)" }}>
                  Unlock the full Forge Feed
                </div>
                <p className="text-sm" style={{ color: "var(--text-2)" }}>
                  Join CannaForge to unlock the full Forge Feed, vote on posts, follow
                  breeder activity, and discover what&rsquo;s happening across the
                  genetics ecosystem. Collector and grower accounts are free to start.
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Link to="/register?next=%2Fforge-feed"
                      className="btn-primary !py-2 !px-4 text-xs inline-flex items-center gap-1.5"
                      data-testid="home-forge-feed-cta-register">
                  Create free account <ArrowRight className="w-3.5 h-3.5" />
                </Link>
                <Link to="/for-breeders"
                      className="btn-outline !py-2 !px-4 text-xs"
                      data-testid="home-forge-feed-cta-breeder">
                  Apply as Breeder
                </Link>
                <Link to="/login?next=%2Fforge-feed"
                      className="btn-outline !py-2 !px-4 text-xs"
                      data-testid="home-forge-feed-cta-login">
                  Sign in
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}




// v3.99d.255 — Current Promotions homepage section.
//
// Reads from GET /api/promotions/public-active (anonymous; safe fields
// only). Renders one card per active promo with promo code, discount
// summary, expiry, eligibility note, and a single CTA tailored to auth
// state. Auction exclusion disclaimer lives at the bottom of the
// section, not on each card. If no active promos exist, the entire
// section is hidden.
/**
 * v3.99d.286 — "Right Now on CannaForge" — Phase 1A acquisition surface.
 *
 * Combines three live-engine groups into one compact section directly
 * under the hero. Each group hides when empty; the entire section
 * hides when all three are empty (the page does not collapse — the
 * existing detailed sections still render further down).
 *
 * Data sources are all REUSED from existing endpoints:
 *   - endingSoon         (already fetched by Home from /marketplace/ending-soon)
 *   - activeGiveaways    (Home now fetches /giveaways?status=active&limit=3)
 *   - promotional packs  (/listings/promotional — same as CurrentPromotionsSection)
 *
 * The existing CurrentPromotionsSection further down still renders the
 * full promotions grid so no promotions are hidden from the page.
 */
export function RightNowSection({ endingSoon, activeGiveaways, publicAuctionsEnabled }) {
  const [promoListings, setPromoListings] = useState([]);
  useEffect(() => {
    api.get("/listings/promotional")
       .then((r) => setPromoListings((r.data && r.data.items) || []))
       .catch(() => setPromoListings([]));
  }, []);

  const showAuctions = publicAuctionsEnabled && endingSoon && endingSoon.length > 0;
  const showGiveaways = activeGiveaways && activeGiveaways.length > 0;
  const showPromos = promoListings && promoListings.length > 0;
  if (!showAuctions && !showGiveaways && !showPromos) return null;

  // Column count: render the section only with the columns that have data.
  const visibleCols = [showAuctions, showGiveaways, showPromos].filter(Boolean).length;
  const gridClass =
    visibleCols === 3 ? "grid grid-cols-1 md:grid-cols-3 gap-6" :
    visibleCols === 2 ? "grid grid-cols-1 md:grid-cols-2 gap-6" :
                        "grid grid-cols-1 gap-6";

  return (
    <section
      className="max-w-[1400px] mx-auto px-6 py-12 border-b border-[var(--border)]"
      data-testid="home-right-now"
    >
      <div className="mb-8">
        <div className="label-eyebrow mb-2 flex items-center gap-2">
          <span className="live-dot" />
          On the platform right now
        </div>
        <h2
          className="font-display text-3xl md:text-4xl tracking-tighter"
          style={{ color: "var(--text)" }}
        >
          Right Now on <span style={{ color: "var(--primary-text)" }}>CannaForge</span>
        </h2>
      </div>
      <div className={gridClass}>
        {showAuctions && (
          <RightNowColumn
            label="Auctions ending soon"
            icon={<Clock className="w-3.5 h-3.5" />}
            seeAllHref="/auctions"
            seeAllLabel="All auctions"
            testid="right-now-auctions"
          >
            {endingSoon.slice(0, 3).map((l) => (
              <Link
                key={l.id}
                to={`/listing/${l.id}`}
                className="block group border-b border-[var(--border)] last:border-b-0 py-3 -mx-1 px-1 hover:bg-[var(--surface)] transition-colors"
                data-testid={`right-now-auction-${l.id}`}
              >
                <div className="flex items-center gap-3">
                  {l.images?.[0] && (
                    <img
                      src={l.images[0]}
                      alt=""
                      loading="lazy"
                      width="48"
                      height="48"
                      className="w-12 h-12 object-cover shrink-0 bg-[var(--surface-2)]"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <div
                      className="font-display text-sm truncate group-hover:text-[var(--primary-text)] transition-colors"
                      style={{ color: "var(--text)" }}
                    >
                      {l.title}
                    </div>
                    <div className="text-[10px] mt-0.5 font-mono uppercase tracking-[0.16em]" style={{ color: "var(--text-muted)" }}>
                      Ends in {timeShort(l.auction_ends_at)} · {l.bid_count || 0} bid{(l.bid_count || 0) === 1 ? "" : "s"}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-[10px] font-mono uppercase tracking-[0.16em]" style={{ color: "var(--text-muted)" }}>
                      Bid
                    </div>
                    <div className="font-mono text-sm font-bold" style={{ color: "var(--primary-text)" }}>
                      ${Number(l.current_bid || l.starting_bid || 0).toFixed(2)}
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </RightNowColumn>
        )}

        {showGiveaways && (
          <RightNowColumn
            label="Active giveaways"
            icon={<Star className="w-3.5 h-3.5" />}
            seeAllHref="/giveaways"
            seeAllLabel="All giveaways"
            testid="right-now-giveaways"
          >
            {activeGiveaways.slice(0, 3).map((g) => (
              <Link
                key={g.id}
                to={`/giveaways/${g.slug || g.id}`}
                className="block group border-b border-[var(--border)] last:border-b-0 py-3 -mx-1 px-1 hover:bg-[var(--surface)] transition-colors"
                data-testid={`right-now-giveaway-${g.id}`}
              >
                <div className="flex items-center gap-3">
                  {(g.banner_url || g.prize_images?.[0]) && (
                    <img
                      src={g.banner_url || g.prize_images[0]}
                      alt=""
                      loading="lazy"
                      width="48"
                      height="48"
                      className="w-12 h-12 object-cover shrink-0 bg-[var(--surface-2)]"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <div
                      className="font-display text-sm truncate group-hover:text-[var(--primary-text)] transition-colors"
                      style={{ color: "var(--text)" }}
                    >
                      {g.title}
                    </div>
                    <div className="text-[10px] mt-0.5 font-mono uppercase tracking-[0.16em]" style={{ color: "var(--text-muted)" }}>
                      {g.entry_count != null ? `${g.entry_count} entered` : "Enter free"}
                      {g.ends_at ? ` · ends ${timeShort(g.ends_at)}` : ""}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div
                      className="text-[10px] font-mono uppercase tracking-[0.16em] px-1.5 py-0.5 border"
                      style={{ borderColor: "var(--gold)", color: "var(--gold)" }}
                    >
                      Free
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </RightNowColumn>
        )}

        {showPromos && (
          <RightNowColumn
            label="Current promotions"
            icon={<Tag className="w-3.5 h-3.5" />}
            seeAllHref="/shop?promotional=1"
            seeAllLabel="View all promotions"
            testid="right-now-promotions"
          >
            {promoListings.slice(0, 3).map((l) => {
              const price = Number(l.price || 0);
              const isFree = !!l.is_free || price <= 0;
              return (
                <Link
                  key={l.id}
                  to={`/listing/${l.id}`}
                  className="block group border-b border-[var(--border)] last:border-b-0 py-3 -mx-1 px-1 hover:bg-[var(--surface)] transition-colors"
                  data-testid={`right-now-promo-${l.id}`}
                >
                  <div className="flex items-center gap-3">
                    {l.images?.[0] && (
                      <img
                        src={l.images[0]}
                        alt=""
                        loading="lazy"
                        width="48"
                        height="48"
                        className="w-12 h-12 object-cover shrink-0 bg-[var(--surface-2)]"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <div
                        className="font-display text-sm truncate group-hover:text-[var(--primary-text)] transition-colors"
                        style={{ color: "var(--text)" }}
                      >
                        {l.title}
                      </div>
                      <div className="text-[10px] mt-0.5 font-mono uppercase tracking-[0.16em]" style={{ color: "var(--text-muted)" }}>
                        {l.store_name || "Promo pack"}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      {isFree ? (
                        <div
                          className="text-[10px] font-mono uppercase tracking-[0.16em] px-1.5 py-0.5 border"
                          style={{ borderColor: "var(--gold)", color: "var(--gold)" }}
                        >
                          Free
                        </div>
                      ) : (
                        <>
                          <div className="text-[10px] font-mono uppercase tracking-[0.16em]" style={{ color: "var(--text-muted)" }}>
                            Pack
                          </div>
                          <div className="font-mono text-sm font-bold" style={{ color: "var(--text)" }}>
                            ${price.toFixed(2)}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </Link>
              );
            })}
          </RightNowColumn>
        )}
      </div>
    </section>
  );
}

function RightNowColumn({ label, icon, seeAllHref, seeAllLabel, children, testid }) {
  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between mb-2 pb-2 border-b-2" style={{ borderColor: "var(--gold)" }}>
        <div
          className="flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-[0.22em]"
          style={{ color: "var(--gold)" }}
        >
          {icon}
          {label}
        </div>
        <Link
          to={seeAllHref}
          className="text-[10px] font-mono uppercase tracking-[0.18em] inline-flex items-center gap-1 hover:opacity-80"
          style={{ color: "var(--text-muted)" }}
          data-testid={`${testid}-see-all`}
        >
          {seeAllLabel} <ArrowRight className="w-3 h-3" />
        </Link>
      </div>
      <div>{children}</div>
    </div>
  );
}

function CurrentPromotionsSection({ user }) {
  const [promos, setPromos] = useState([]);
  const [promoListings, setPromoListings] = useState([]);
  useEffect(() => {
    api.get("/promotions/public-active")
       .then((r) => setPromos((r.data && r.data.items) || []))
       .catch(() => setPromos([]));
    // v3.99d.263 — Also surface promotional packs (promotional_product=true
    // listings) so collectors see freebies + samplers alongside promo-code
    // offers. Auctions categorically excluded by the endpoint.
    api.get("/listings/promotional")
       .then((r) => setPromoListings((r.data && r.data.items) || []))
       .catch(() => setPromoListings([]));
  }, []);
  if (!promos.length && !promoListings.length) return null;
  return (
    <section className="max-w-[1400px] mx-auto px-6 pt-10 pb-2"
             data-testid="home-current-promotions">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="label-eyebrow mb-2 flex items-center gap-2">
            <Tag className="w-3 h-3" style={{ color: "#D4AF37" }} />
            Current Promotions
          </div>
          <h2 className="font-display text-2xl md:text-3xl tracking-tighter"
              style={{ color: "var(--text)" }}>
            Live offers on CannaForge
          </h2>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
           data-testid="home-current-promotions-grid">
        {/* Promotional listing cards first — gold gift packs draw the eye
            harder than discount codes. */}
        {promoListings.map((l) => (
          <PromoListingCard key={`pl-${l.id}`} listing={l} loggedIn={!!user} />
        ))}
        {promos.map((p) => (
          <PromoCard key={p.id} promo={p} loggedIn={!!user} />
        ))}
      </div>
      <p className="text-[10px] italic mt-4 leading-snug"
         style={{ color: "var(--text-muted)" }}
         data-testid="home-current-promotions-disclaimer">
        Promo codes and promotional packs apply to eligible shop items only.
        Auction wins are excluded.
      </p>
    </section>
  );
}

/**
 * v3.99d.263 — Promotional listing card. Distinct visual treatment from
 * promo-code cards: gold-bordered hero with Gift icon, listing title +
 * breeder, price (or "FREE" badge when price=0), and a "Claim now" CTA
 * that deep-links to the listing detail page.
 */
function PromoListingCard({ listing, loggedIn }) {
  const price = Number(listing.price || 0);
  const isFree = !!listing.is_free || price <= 0;
  const oneStockLeft = listing.quantity > 0 && listing.quantity <= 5;
  const ctaLabel = loggedIn
    ? (isFree ? "Claim free pack" : "View details")
    : "Sign in to claim";
  const ctaTo = loggedIn
    ? `/listing/${listing.slug || listing.id}`
    : "/register";
  return (
    <Link
      to={ctaTo}
      className="border p-4 flex flex-col gap-2 hover:bg-[rgba(212,175,55,0.04)] transition-colors group"
      style={{ borderColor: "#D4AF37",
               background: "linear-gradient(135deg, rgba(212,175,55,0.08) 0%, var(--surface) 100%)" }}
      data-testid={`home-promo-listing-${listing.id}`}>
      <div className="flex items-center gap-2 justify-between">
        <span className="font-mono text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 border inline-flex items-center gap-1"
              style={{ borderColor: "#D4AF37",
                       color: "#D4AF37",
                       background: "rgba(212,175,55,0.10)" }}>
          <Gift className="w-3 h-3"/>
          {isFree ? "Free pack" : "Promo pack"}
        </span>
        {oneStockLeft && (
          <span className="text-[10px] font-mono"
                style={{ color: "#E66B3A" }}>
            Only {listing.quantity} left
          </span>
        )}
      </div>
      {/* v3.99d.289 — small thumbnail + title row. The thumb makes
          each Live Offers card scannable; the gold ring stays the
          dominant visual signal. */}
      <div className="flex gap-3 items-start">
        {listing.images?.[0] && (
          <img src={listing.images[0]} alt="" loading="lazy"
               width="56" height="56"
               className="w-14 h-14 object-cover shrink-0 bg-[var(--surface-2)] border border-[var(--border)]"
               style={{ borderRadius: 2 }}
               data-testid={`home-promo-listing-${listing.id}-thumb`} />
        )}
        <div className="flex-1 min-w-0">
          <div className="font-serif text-base leading-tight"
               style={{ color: "var(--text)" }}>
            {listing.title}
          </div>
          <div className="text-[11px] mt-0.5"
               style={{ color: "var(--text-muted)" }}>
            by <span style={{ color: "var(--primary-text)" }}>{listing.store_name}</span>
            {listing.seed_count && <> · {listing.seed_count}-pack</>}
        </div>
      </div>
      </div>
      {/* Price + per-customer tag */}
      <div className="flex items-end gap-2 mt-1">
        {isFree ? (
          <span className="font-display text-2xl tracking-tighter"
                style={{ color: "#7FCB99" }}>
            FREE
          </span>
        ) : (
          <span className="font-mono text-lg font-semibold"
                style={{ color: "#D4AF37" }}>
            ${price.toFixed(2)}
          </span>
        )}
        {listing.one_per_customer && (
          <span className="text-[10px] font-mono uppercase tracking-wide opacity-80"
                style={{ color: "var(--text-muted)" }}>
            · 1 per customer
          </span>
        )}
      </div>
      <span className="btn-outline mt-2 self-start text-xs"
            data-testid={`home-promo-listing-cta-${listing.id}`}>
        {ctaLabel} <ArrowRight className="w-3 h-3 ml-1 inline" />
      </span>
    </Link>
  );
}

function PromoCard({ promo, loggedIn }) {
  const value = (() => {
    const t = promo.discount_type;
    const v = Number(promo.discount_value || 0);
    if (t === "percentage") return `${Math.round(v)}% off`;
    if (t === "fixed") return `$${v.toFixed(2)} off`;
    if (t === "free_shipping") return "Free shipping";
    return "";
  })();
  const endsLabel = (() => {
    if (!promo.ends_at) return "Limited time";
    try {
      const d = new Date(promo.ends_at);
      return `Ends ${d.toLocaleDateString(undefined,
        { month: "short", day: "numeric" })}`;
    } catch { return ""; }
  })();
  const cta = loggedIn
    ? { label: "Shop eligible items", to: "/browse",
        testid: "promo-card-cta-shop" }
    : { label: "Create free account", to: "/register",
        testid: "promo-card-cta-register" };
  return (
    <div className="border p-4 flex flex-col gap-2"
         style={{ borderColor: "var(--border)",
                  background: "var(--surface)" }}
         data-testid={`home-promo-card-${promo.id}`}>
      <div className="flex items-center gap-2 justify-between">
        <span className="font-mono text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 border"
              style={{ borderColor: "#D4AF3755",
                       color: "#D4AF37",
                       background: "rgba(212,175,55,0.06)" }}>
          {value}
        </span>
        <span className="text-[10px] font-mono"
              style={{ color: "var(--text-muted)" }}>
          {endsLabel}
        </span>
      </div>
      <div className="font-serif text-base leading-tight"
           style={{ color: "var(--text)" }}>
        {promo.name}
      </div>
      {promo.description && (
        <div className="text-[11px] leading-snug"
             style={{ color: "var(--text-2)" }}>
          {promo.description}
        </div>
      )}
      {promo.code && (
        <div className="mt-1 inline-flex items-center gap-2 self-start
                        px-2.5 py-1 border-2 border-dashed font-mono text-xs tracking-[0.18em]"
             style={{ borderColor: "#D4AF37",
                      color: "#D4AF37",
                      background: "rgba(212,175,55,0.04)" }}
             data-testid={`home-promo-code-${promo.id}`}>
          <Tag className="w-3 h-3" />
          {promo.code}
        </div>
      )}
      {promo.max_discount_amount > 0 && promo.discount_type !== "free_shipping" && (
        <div className="text-[10px]" style={{ color: "var(--text-muted)" }}>
          Max ${Number(promo.max_discount_amount).toFixed(2)} off per item
        </div>
      )}
      {promo.min_order_value > 0 && (
        <div className="text-[10px]" style={{ color: "var(--text-muted)" }}>
          Minimum order ${Number(promo.min_order_value).toFixed(2)}
        </div>
      )}
      <Link to={cta.to}
            className="btn-outline mt-2 self-start text-xs"
            data-testid={cta.testid}>
        {cta.label} <ArrowRight className="w-3 h-3 ml-1 inline" />
      </Link>
    </div>
  );
}
